import React, { useEffect, useRef, useState } from 'react';
import emailIcon from '../assets/contact-us/email.png';
import phoneIcon from '../assets/contact-us/phone.png';
import locationIcon from '../assets/contact-us/location.png';
import fbIcon from '../assets/contact-us/facebook-app-symbol.png';
import instaIcon from '../assets/contact-us/instagram.png';
import linkedinIcon from '../assets/contact-us/linkedin.png';
import mapOverlay from '../assets/contact-us/map-overlay.png';

const ContactUs = () => {
  const [inView, setInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [headingText, setHeadingText] = useState('');
  const sectionRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const URL = process.env.REACT_APP_BACKEND
      const response = await fetch(`${URL}/api/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Clear form
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Typewriter effect for heading
  useEffect(() => {
    if (!inView) return;

    const text = "Get In Touch";
    let currentIndex = 0;

    const typingInterval = setInterval(() => {
      if (currentIndex <= text.length) {
        setHeadingText(text.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100); // Typing speed (100ms per character)

    return () => clearInterval(typingInterval);
  }, [inView]);

  return (
    <section ref={sectionRef} className="text-white py-16 px-6 lg:px-24">
      <style>
        {`
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideLeft {
            from { 
              opacity: 0;
              transform: translateX(-100px);
            }
            to { 
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes slideRight {
            from { 
              opacity: 0;
              transform: translateX(100px);
            }
            to { 
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .animate-slide-up {
            animation: slideUp 0.8s ease-out forwards;
          }
          
          .animate-slide-left {
            animation: slideLeft 0.8s ease-out forwards;
          }
          
          .animate-slide-right {
            animation: slideRight 0.8s ease-out forwards;
          }

          .typewriter-cursor {
            display: inline-block;
            width: 2px;
            height: 1em;
            background-color: white;
            margin-left: 2px;
            vertical-align: text-bottom;
            animation: blinkCursor 0.7s infinite;
          }
        `}
      </style>

      {/* Heading */}
      <div
        className={`text-center max-w-3xl mx-auto ${inView ? 'opacity-100 animate-slide-up' : 'opacity-0'
          }`}
        style={{ animationDelay: '0ms' }}
      >
        <h2 className="text-4xl lg:text-5xl font-bold min-h-[3rem]">
          {headingText}
          {inView && headingText.length < "Get In Touch".length && (
            <span className="typewriter-cursor" />
          )}
        </h2>
        <p className="mt-4 text-lg lg:text-xl">Let's move your business forward!</p>
        <p className="mt-2 text-sm lg:text-base">
          Whether you’re an importer, exporter or exploring logistics for the first time,
          we’re ready to help.
        </p>
      </div>

      {/* Content */}
      <div className="mt-12 flex flex-col justify-center md:flex-row gap-8">
        {/* Left Card */}
        <div
          className={`md:w-1/2 lg:w-[50%] xl:w-[35%] 2xl:w-[30%] ${inView ? (isMobile ? 'animate-slide-up' : 'animate-slide-left') : 'opacity-0'
            }`}
          style={{ animationDelay: isMobile ? '100ms' : '200ms' }}
        >
          <div
            className="relative h-full rounded-3xl shadow-[0_0_60px_rgba(255,255,255,0.2)] object-cover bg-cover opacity-100 overflow-hidden"
            style={{ backgroundImage: `url(${mapOverlay})` }}
          >
            {/* Gradient Overlay */}
            <div
              className="absolute inset-0 rounded-3xl mix-blend-overlay"
              style={{
                backgroundImage:
                  'linear-gradient(to right, rgba(3, 186, 244, 0.2), rgba(3, 186, 244, 0.4))',
              }}
            ></div>

            {/* Card Content */}
            <div className="relative z-10 p-8 flex flex-col justify-between 2xl:items-center h-full">
              <div>
                <h3 className="text-3xl lg:text-4xl font-bold mb-16">
                  We're Always Here<br />To Respond.
                </h3>
                <ul className="space-y-10 text-sm lg:text-base ">
                  <li className="flex items-center">
                    <img src={locationIcon} alt="Location" className="w-6 h-6 mr-3" />
                    2550 Argentia Road, Mississauga, ON, L5N 5R1 Canada
                  </li>
                  <li className="flex items-center">
                    <img src={phoneIcon} alt="Phone" className="w-6 h-6 mr-3" />
                    +1-289-835-7593
                  </li>
                  <li className="flex items-center">
                    <img src={emailIcon} alt="Email" className="w-6 h-6 mr-3" />
                    salessupport@bigblueinc.ca<br />customerservice@bigblueinc.ca
                  </li>
                </ul>
              </div>

              <div className="mt-6">

                <div className="justify-center mt-0 flex space-x-4">
                  <a href="https://www.instagram.com/bigblue_inc?igsh=bXBkMnBjZzlwNWFi&utm_source=qr">
                    <img src={instaIcon} alt="Instagram" className="w-8 h-8" />
                  </a>
                  <a href="https://www.linkedin.com/company/big-blue-inc/">
                    <img src={linkedinIcon} alt="LinkedIn" className="w-8 h-8" />
                  </a>
                  <a href="https://www.facebook.com/share/16VL31FUML/?mibextid=wwXIfr">
                    <img src={fbIcon} alt="Facebook" className="w-8 h-8" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Form */}
        <div
          className={`md:w-1/2 lg:w-[35%] ${inView ? (isMobile ? 'animate-slide-up' : 'animate-slide-right') : 'opacity-0'
            }`}
          style={{ animationDelay: isMobile ? '200ms' : '400ms' }}
        >
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-100 text-green-700 rounded-lg">
                Message sent successfully! We'll get back to you soon.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="p-4 bg-red-100 text-red-700 rounded-lg">
                Error sending message. Please try again later.
              </div>
            )}

            {/* Full Name */}
            <div>
              <label className="block text-sm mb-2" htmlFor="firstName">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  id="firstName"
                  placeholder="First Name"
                  className="w-full border border-white rounded-full px-4 py-2 bg-transparent focus:outline-none"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <input
                  type="text"
                  id="lastName"
                  placeholder="Last Name"
                  className="w-full border border-white rounded-full px-4 py-2 bg-transparent focus:outline-none"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm mb-2" htmlFor="phone">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                placeholder="(123) 456-7890"
                className="w-full border border-white rounded-full px-4 py-2 bg-transparent focus:outline-none"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            {/* E-mail */}
            <div>
              <label className="block text-sm mb-2" htmlFor="email">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                placeholder="you@example.com"
                className="w-full border border-white rounded-full px-4 py-2 bg-transparent focus:outline-none"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm mb-2" htmlFor="message">
                Message
              </label>
              <textarea
                id="message"
                rows={6}
                placeholder="Write your message here..."
                className="w-full border border-white rounded-xl px-4 py-3 bg-transparent focus:outline-none"
                value={formData.message}
                onChange={handleChange}
              ></textarea>
            </div>

            {/* Submit */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-blue-400 hover:bg-blue-300 text-blue-950 px-6 py-3 rounded-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-950 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : 'Send A Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;